import React, {useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authcontext";
import { useFlash } from "../../context/flashcontext";
import { useCustomerList } from "../../hooks/useCustomerList";
import CustomerListView from "./CustomerListView";
import {useKvKLookup} from "../../hooks/useKvKLookup";

function CustomerListContainer() {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const { showFlash } = useFlash();
    const navigate = useNavigate();
    const customerList = useCustomerList();
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [vatError, setVatError] = useState("");
    const [newCustomer, setNewCustomer] = useState({
        name: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        kvk_number: "",
        btw_id: "",
        contact_persons: [] // NEW: Array for new contact person system
    });
    const [creating, setCreating] = useState(false);
    const { kvkError, loadingKvk, fetchKvKData } = useKvKLookup(setNewCustomer, showFlash, true);

    const resetForm = () => {
        setNewCustomer({
            name: "",
            contact_person: "",
            email: "",
            phone: "",
            address: "",
            kvk_number: "",
            btw_id: "",
            contact_persons: []
        });
        setVatError("");
        setShowCreateForm(false);
    };

    const handleViewCustomer = (id) => {
        navigate(`/customers/${id}`);
    };

    const handleDeleteCustomer = (id) => {
        setCustomerToDelete(id);
        setShowDeleteModal(true);
    };

    const validateDutchVat = (vat) => /^NL\d{9}B\d{2}$/.test(vat);

    const handleVatChange = (e) => {
        handleNewCustomerChange(e);
        const value = e.target.value;
        if (value && !validateDutchVat(value)) {
            setVatError("Ongeldig Nederlands BTW-nummer (bijv. NL123456789B01)");
        } else {
            setVatError("");
        }
    };

    const confirmDeleteCustomer = async () => {
        try {
            await customerList.deleteCustomer(customerToDelete);
            showFlash(t('customers.customerDeletedSuccess'), "success");
        } catch (err) {
            // Show specific error message if provided, otherwise generic error
            const errorMessage = err.message || t('customers.customerDeletedError');
            showFlash(errorMessage, "error");
        } finally {
            setShowDeleteModal(false);
            setCustomerToDelete(null);
        }
    };

    const cancelDeleteCustomer = () => {
        setShowDeleteModal(false);
        setCustomerToDelete(null);
    };

    const handleCreateCustomer = async (e) => {
        e.preventDefault();
        setCreating(true);
        if (kvkError || vatError) {
            showFlash("Corrigeer eerst de KvK- of BTW-nummer fouten.", "error");
            setCreating(false);
            return;
        }

        // Validate only required fields
        const requiredFields = {
            name: "Naam",
            kvk_number: "KVK-Nummer",
            address: "Adres",
            btw_id: "BTW-ID"
        };

        for (const [field, label] of Object.entries(requiredFields)) {
            if (!newCustomer[field] || newCustomer[field].trim() === "") {
                showFlash(`${label} mag niet leeg zijn.`, "error");
                setCreating(false);
                return;
            }
        }
        try {
            await customerList.createCustomer(newCustomer);
            showFlash("Klant succesvol aangemaakt.", "success");
            resetForm();
        } catch (err) {
            showFlash("Fout bij aanmaken van klant.", "error");
        } finally {
            setCreating(false);
        }
    };

    // Automate KvK lookup when more than 3 characters are filled in kvk_number
    useEffect(() => {
        if (newCustomer.kvk_number && newCustomer.kvk_number.length > 3) {
            fetchKvKData(newCustomer.kvk_number);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newCustomer.kvk_number]);

    const handleNewCustomerChange = (e) => {
        const { name, value } = e.target;
        setNewCustomer((prev) => ({ ...prev, [name]: value }));
    };

    // Add a contact person to the new customer
    const handleAddContactPerson = (contactPerson) => {
        setNewCustomer(prev => ({
            ...prev,
            contact_persons: [...prev.contact_persons, contactPerson]
        }));
    };

    // Remove a contact person from the new customer
    const handleRemoveContactPerson = (index) => {
        setNewCustomer(prev => ({
            ...prev,
            contact_persons: prev.contact_persons.filter((_, i) => i !== index)
        }));
    };

    // Update a contact person in the new customer
    const handleUpdateContactPerson = (index, contactPerson) => {
        setNewCustomer(prev => ({
            ...prev,
            contact_persons: prev.contact_persons.map((cp, i) => i === index ? contactPerson : cp)
        }));
    };

    return (
        <CustomerListView
            {...customerList}
            auth={auth}
            onViewCustomer={handleViewCustomer}
            onDeleteCustomer={handleDeleteCustomer}
            showDeleteModal={showDeleteModal}
            confirmDeleteCustomer={confirmDeleteCustomer}
            cancelDeleteCustomer={cancelDeleteCustomer}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            resetForm={resetForm}
            newCustomer={newCustomer}
            onNewCustomerChange={handleNewCustomerChange}
            onCreateCustomer={handleCreateCustomer}
            creating={creating}
            handleVatChange={handleVatChange}
            vatError={vatError}
            kvkError={kvkError}
            loadingKvk={loadingKvk}
            onKvKLookup={() => fetchKvKData(newCustomer.kvk_number)}
            onAddContactPerson={handleAddContactPerson}
            onRemoveContactPerson={handleRemoveContactPerson}
            onUpdateContactPerson={handleUpdateContactPerson}
        />
    );
}

export default CustomerListContainer;

CustomerListContainer.propTypes = {
    // No props - uses hooks internally
};
